import { useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { Paper, Text, Progress, Stack, Accordion, Group, ThemeIcon, Grid, Flex, Box, Badge, Button, Checkbox, Skeleton } from '@mantine/core';
import { IconCheck, IconExclamationMark, IconArrowRight } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import AuthContext from 'context/AuthContext';
import { IAvailabilityTime, ITeamMember } from 'interfaces/Agent';
import routes from 'utils/routes';
import { getOnboardingProgress, updateOnboardingProgress, OnboardingProgress } from 'services/ApiService';

interface TodoItem {
  id: string;
  title: string;
  description: string;
  completed: boolean;
  step?: number;
  skipped?: boolean;
}

interface SetupProgressProps {
  tasks?: TodoItem[];
}

const setupSections = [
  {
    id: 'training',
    title: 'Add Training Data',
    description: 'Train your AI receptionist with your business knowledge',
    todos: [
      {
        id: 'business-name',
        title: 'Business name',
        description: 'Set your business name to personalize interactions and build trust with customers.',
        completed: false
      },
      {
        id: 'welcome-message',
        title: 'Welcome message',
        description: 'Create a warm welcome message for your customers to make a great first impression!',
        completed: false
      },
      {
        id: 'goodbye-message',
        title: 'Goodbye message',
        description: 'Set a professional farewell message to end conversations on a positive note.',
        completed: false
      },
      {
        id: 'business-info',
        title: 'Business Information',
        description: 'Complete your business profile with essential info like hours, location, and services to enhance customer trust.',
        completed: false,
        step: 3
      }
    ]
  },
  {
    id: 'staff',
    title: 'Staff Directory',
    description: 'Help your receptionist manage calls by adding your team',
    todos: [
      {
        id: 'staff-add',
        title: 'Add the rest of your staff',
        description: 'Add all your team members to ensure calls are directed to the right person.',
        completed: false
      },
      {
        id: 'staff-roles',
        title: 'Define staff roles',
        description: 'Set up roles and responsibilities for each team member to improve call routing.',
        completed: false
      },
      {
        id: 'staff-scheduling',
        title: 'Setup staff scheduling',
        description: 'Configure working hours and availability to ensure calls are handled during business hours.',
        completed: false
      }
    ]
  },
  {
    id: 'share',
    title: 'Share Your Number',
    description: 'Start using your AI phone system',
    todos: [
      {
        id: 'update-online',
        title: 'Update your website, social profiles, and Google Business Profile',
        description: 'Update your contact page, "call now" buttons, any links in social bios, and Google Business Profile where applicable',
        completed: false
      },
      {
        id: 'promote-number',
        title: 'Promote your new number everywhere',
        description: 'Update email signatures, invoices, contracts, printed marketing materials, and voicemail for existing business phone number: "Call our new AI-powered business line XXX-XXX-XXXX for instant 24/7 support."',
        completed: false
      },
      {
        id: 'social-media',
        title: 'Share on social media',
        description: 'Offer an exclusive benefit to your followers for calling the new number. For example, "Call our new AI receptionist at XXX-XXX-XXXX and ask for our secret discount code for 20% off your next order."',
        completed: false
      },
      {
        id: 'email-customers',
        title: 'Email your existing customers',
        description: 'Encourage and incentivise your customers to call your new number. "Call our new number for faster service and special offers!"',
        completed: false
      },
      {
        id: 'forward-number',
        title: 'Forward your existing business phone number',
        description: 'Contact the carrier of your existing business phone number with a request to forward your number to your Nucleus phone number.',
        completed: false
      }
    ]
  }
];

// Minimum required characters for business information
const BUSINESS_INFO_THRESHOLD = 750;

const getNavigationConfig = (todoId: string) => {
  switch (todoId) {
    case 'business-name':
    case 'welcome-message':
    case 'goodbye-message':
    case 'business-info':
      return {
        path: routes.receptionPath.href,
        tab: 'training'
      };
    case 'staff-add':
    case 'staff-roles':
    case 'staff-scheduling':
      return {
        path: routes.receptionPath.href,
        tab: 'staff-directory'
      };
    default:
      return null;
  }
};

// New function to get title and description based on progress
const getProgressContent = (progress: number) => {
  if (progress === 100) {
    return {
      title: "Setup Complete",
      description: "Your receptionist is fully configured and ready to impress. You can make updates anytime."
    };
  } else if (progress >= 67 && progress < 100) {
    return {
      title: "Almost There!",
      description: "Just a couple more steps to unlock the full power of your receptionist."
    };
  } else if (progress >= 34 && progress < 67) {
    return {
      title: "You're Making Great Progress",
      description: "Keep it up! Each step gives your receptionist more tools to deliver a better customer experience."
    };
  } else {
    return {
      title: "Let's Get Started",
      description: "Start with a few essentials so your AI receptionist can begin handling calls with confidence."
    };
  }
};

export function SetupProgress({ tasks }: SetupProgressProps) {
  const navigate = useNavigate();
  const { data: { company } } = useContext(AuthContext);

  // --- Onboarding Progress State ---
  const [onboardingProgress, setOnboardingProgress] = useState<OnboardingProgress>({ onboarding: {} });
  const [loadingOnboarding, setLoadingOnboarding] = useState(true);
  const [errorOnboarding, setErrorOnboarding] = useState<string | null>(null);

  // --- State for controlled Accordion ---
  const [activeAccordionItem, setActiveAccordionItem] = useState<string | null>(null);

  // Fetch onboarding progress on mount
  useEffect(() => {
    const fetchProgress = async () => {
      setLoadingOnboarding(true);
      setErrorOnboarding(null);
      try {
        const progress = await getOnboardingProgress();
        // console.log('Fetched onboarding progress:', progress); // Removed log
        // Ensure onboarding object exists
        setOnboardingProgress({
          ...progress,
          onboarding: progress.onboarding || {} // Ensure onboarding exists
        });
      } catch (err) {
        console.error('Failed to load onboarding progress:', err);
        setErrorOnboarding('Failed to load onboarding progress');
        // Use empty progress object as fallback
        setOnboardingProgress({ onboarding: {} });
      } finally {
        setLoadingOnboarding(false);
      }
    };
    fetchProgress();
  }, []);

  // Helper to update onboarding progress (PUT)
  const saveOnboardingProgress = useCallback(async (newProgress: OnboardingProgress) => {
    // setLoadingOnboarding(true); // REMOVED - Don't trigger global loading for updates
    setErrorOnboarding(null);
    try {
      const updated = await updateOnboardingProgress(newProgress);
      // console.log('Updated onboarding progress:', updated); // Removed log
      setOnboardingProgress(updated);
      
      // Dispatch a custom event to notify other components
      const event = new CustomEvent('onboarding-progress-updated', { detail: updated });
      document.dispatchEvent(event);
      
      return true;
    } catch (err) {
      console.error('Failed to update onboarding progress:', err);
      setErrorOnboarding('Failed to update onboarding progress');
      // Keep local state updated even if API fails
      setOnboardingProgress(newProgress);
      return false;
    } finally {
      // setLoadingOnboarding(false); // REMOVED - Don't trigger global loading for updates
    }
  }, []);

  // Helper function to check if a task is skipped (Moved before useMemo)
  const isTaskSkipped = useCallback((taskId: string) => {
    const taskState = onboardingProgress?.onboarding?.[taskId];
    return taskState?.user_override === 'skipped'; // DB value is 'skipped'
  }, [onboardingProgress]);

  // Check if a task is completed based on onboarding progress
  const isTaskCompleted = useCallback((taskId: string) => {
    // Safely access the task state - handle missing onboarding object
    const taskState = onboardingProgress?.onboarding?.[taskId];
    
    // If user override exists, respect it
    if (taskState?.user_override === 'complete') return true;
    if (taskState?.user_override === 'skipped') return true; // Consider skipped tasks as "completed" for UI
    
    // Otherwise use the actual completion state
    return !!taskState?.completed;
  }, [onboardingProgress]);

  // Update a specific task's completion status
  const updateTaskCompletion = useCallback(async (
    taskId: string, 
    completed: boolean, 
    userOverride: 'complete' | 'skipped' | null = null
  ) => {
    // Create a copy of the current progress with safe defaults
    const newProgress = { 
      ...onboardingProgress,
      onboarding: { ...(onboardingProgress?.onboarding || {}) } 
    };
    
    // Initialize the task if it doesn't exist
    if (!newProgress.onboarding[taskId]) {
      newProgress.onboarding[taskId] = { completed: false };
    }
    
    // Update the task state
    newProgress.onboarding[taskId] = {
      ...newProgress.onboarding[taskId],
      completed,
      completed_at: completed ? new Date().toISOString() : undefined,
      user_override: userOverride as 'complete' | 'skipped' | null
    };
    
    // Update timestamp and version
    newProgress.last_updated = new Date().toISOString();
    newProgress.version = (newProgress.version || 0) + 1;
    
    // Save to API
    return saveOnboardingProgress(newProgress);
  }, [onboardingProgress, saveOnboardingProgress]);

  // Check if business name and greetings are set
  const hasBusinessName = Boolean(company?.name);
  const hasWelcomeMessage = Boolean(company?.welcome_message);
  const hasGoodbyeMessage = Boolean(company?.goodbye_message);

  // Staff directory checks
  const hasMultipleStaff = (company?.users?.length || 0) > 1;
  const hasStaffWithRoles = company?.users?.some((member: ITeamMember) => {
    // Check if the member has at least one role tag defined
    // Check for roles in the tags array
    return Boolean(member.tags?.length);
  });
  
  // For debugging purposes, log out if any staff has roles
  const hasScheduledStaff = !!company?.users?.some((member: ITeamMember) => {
    if (member.status !== 'schedule' || !member.availability) return false;
    
    // Type assertion for the availability object
    const availabilityDays = Object.values(member.availability) as IAvailabilityTime[];
    return availabilityDays.some(day => day.enabled);
  });

  // Calculate total characters in business information with safe access
  const getTotalChars = () => {
    const cards = company?.cards || [];
    const total = cards.reduce((total: number, card: { title?: string; body?: string }) => {
      const titleLength = card?.title?.length || 0;
      const bodyLength = card?.body?.length || 0;
      return total + titleLength + bodyLength;
    }, 0);
    
    // console.log('Business Info Characters:', { // Removed log
    //   total,
    //   threshold: BUSINESS_INFO_THRESHOLD,
    //   cards: cards.length,
    //   meetsThreshold: total >= BUSINESS_INFO_THRESHOLD
    // });
    
    return total;
  };

  // Check if business information meets the minimum threshold
  const totalChars = getTotalChars();
  const hasValidBusinessInfo = totalChars >= BUSINESS_INFO_THRESHOLD;

  // This useEffect syncs UI-calculated states with the API
  // Only runs when the UI-calculated states change, not on every render
  useEffect(() => {
    // Skip during initial loading
    if (loadingOnboarding) return;
    
    // Create a copy of current progress
    const newProgress = { 
      ...onboardingProgress,
      onboarding: { ...(onboardingProgress.onboarding || {}) } 
    };
    let hasChanges = false;

    // Check each todo and sync if the calculated state differs from stored state
    setupSections.forEach(section => {
      section.todos.forEach(todo => {
        // Skip items that have user overrides
        const currentState = newProgress.onboarding[todo.id];
        if (currentState?.user_override) return;
        
        // Determine the calculated state based on UI data
        let calculatedCompletion = false;
        switch (todo.id) {
          case 'business-name':
            calculatedCompletion = hasBusinessName;
            break;
          case 'welcome-message':
            calculatedCompletion = hasWelcomeMessage;
            break;
          case 'goodbye-message':
            calculatedCompletion = hasGoodbyeMessage;
            break;
          case 'business-info':
            calculatedCompletion = hasValidBusinessInfo;
            break;
          case 'staff-add':
            calculatedCompletion = hasMultipleStaff;
            break;
          case 'staff-roles':
            calculatedCompletion = !!hasStaffWithRoles;
            break;
          case 'staff-scheduling':
            calculatedCompletion = !!hasScheduledStaff;
            break;
          default:
            // For "share" section items, ensure they have an entry in the API
            // We keep their existing completion status or default to false
            if (section.id === 'share') {
              calculatedCompletion = !!currentState?.completed;
            }
            break;
        }
        
        // If we don't have this task in API yet, initialize it with appropriate defaults
        if (!currentState) {
          hasChanges = true;
          newProgress.onboarding[todo.id] = {
            completed: calculatedCompletion,
            completed_at: calculatedCompletion ? new Date().toISOString() : undefined
          };
        } 
        // Or if completion status differs from what's in the API
        else if (currentState.completed !== calculatedCompletion) {
          // Only update if we're transitioning from uncompleted to completed
          // This preserves completion status even if conditions change later
          if (calculatedCompletion && !currentState.completed) {
            hasChanges = true;
            newProgress.onboarding[todo.id] = {
              ...currentState,
              completed: calculatedCompletion,
              completed_at: calculatedCompletion ? new Date().toISOString() : undefined
            };
        }
      }
      });
    });
    
    // Only update API if we have changes
    if (hasChanges) {
      newProgress.last_updated = new Date().toISOString();
      newProgress.version = (newProgress.version || 0) + 1;
      
      // console.log('Syncing UI calculated state to API:', newProgress); // Removed log
      saveOnboardingProgress(newProgress).catch((err: any) => {
        console.error('Failed to sync UI state to API:', err);
      });
    }
  }, [
    loadingOnboarding,
    onboardingProgress,
    hasBusinessName,
    hasWelcomeMessage,
    hasGoodbyeMessage,
    hasValidBusinessInfo,
    hasMultipleStaff,
    hasStaffWithRoles,
    hasScheduledStaff,
    saveOnboardingProgress
  ]);

  // Special effect to fix the 'staff-roles' task if it's incorrectly marked as completed
  useEffect(() => {
    // Skip during initial loading
    if (loadingOnboarding) return;
    
    // Check if we need to reset the staff-roles task if it has a user override but no staff actually has roles
    const staffRolesTask = onboardingProgress?.onboarding?.['staff-roles'];
    const hasUserOverride = staffRolesTask?.user_override === 'complete';
    
    // Only reset manually completed items, not auto-completed ones
    if (hasUserOverride && !hasStaffWithRoles) {
      // console.log('Resetting staff-roles task because no staff has roles and it has a user override'); // Commented out
      updateTaskCompletion('staff-roles', false, null);
    }
  }, [loadingOnboarding, onboardingProgress, hasStaffWithRoles, updateTaskCompletion]);

  // Add a button to manually mark a task as complete or incomplete
  const handleMarkComplete = useCallback(async (todoId: string, isCompleted: boolean) => {
    // If the task is currently skipped (DB uses 'skipped') and we're trying to mark it as complete,
    // we need to change the user_override from 'skipped' to 'complete'
    const taskState = onboardingProgress?.onboarding?.[todoId];
    const isCurrentlySkipped = taskState?.user_override === 'skipped';
    
    // If the task is skipped and we're marking it as complete, 
    // force the completed state and use 'complete' override
    if (isCurrentlySkipped && isCompleted) {
      await updateTaskCompletion(todoId, true, 'complete');
    } else {
      // Otherwise, follow normal behavior
      await updateTaskCompletion(todoId, isCompleted, isCompleted ? 'complete' : null);
    }
  }, [onboardingProgress, updateTaskCompletion]);

  // Add a handler for skipping tasks
  const handleSkipTask = useCallback(async (todoId: string) => {
    // Use 'skipped' as the user_override to mark task as skipped (DB uses 'skipped')
    await updateTaskCompletion(todoId, true, 'skipped');
  }, [updateTaskCompletion]);

  // Add sync effect for auto-transitioning calculated items from skipped to completed
  useEffect(() => {
    // Skip if still loading
    if (loadingOnboarding) return;
    
    // For any calculated items that are skipped but now should be completed based on UI state,
    // automatically update them to be completed and remove the skipped override
    const calculatedItems = [
      { id: 'business-name', completed: hasBusinessName },
      { id: 'welcome-message', completed: hasWelcomeMessage },
      { id: 'goodbye-message', completed: hasGoodbyeMessage },
      { id: 'business-info', completed: hasValidBusinessInfo },
      { id: 'staff-add', completed: hasMultipleStaff },
      { id: 'staff-roles', completed: !!hasStaffWithRoles },
      { id: 'staff-scheduling', completed: !!hasScheduledStaff }
    ];
    
    // Check each calculated item
    calculatedItems.forEach(item => {
      // If the API says it's skipped (API uses 'skipped', UI uses 'skipped')
      const apiTask = onboardingProgress?.onboarding?.[item.id];
      const isSkipped = apiTask?.user_override === 'skipped';
      
      // But UI says it should be completed based on the data
      if (isSkipped && item.completed) {
        // console.log(`Auto-transitioning ${item.id} from skipped to completed`); // Commented out
        // Remove the skip override and mark as completed
        updateTaskCompletion(item.id, true, 'complete');
      }
    });
  }, [
    loadingOnboarding,
    onboardingProgress,
    hasBusinessName,
    hasWelcomeMessage,
    hasGoodbyeMessage,
    hasValidBusinessInfo,
    hasMultipleStaff,
    hasStaffWithRoles,
    hasScheduledStaff,
    updateTaskCompletion
  ]);

  const handleNavigation = (todoId: string) => {
    const config = getNavigationConfig(todoId);
    if (config) {
      // Mark this task as "in progress" in the onboarding data
      updateTaskCompletion(todoId, false);
      
      // Construct path with tab query parameter if it exists
      const targetPath = config.tab ? `${config.path}?tab=${config.tab}` : config.path;
      navigate(targetPath);
    }
  };

  // Calculate progress and derived states using useMemo
  const { 
    progress: calculatedProgress, 
    title: calculatedTitle, 
    description: calculatedDescription, 
    mappedSections: calculatedSections, 
  } = useMemo(() => {
    if (loadingOnboarding) {
      return { 
        progress: 0, 
        title: 'Loading...', 
        description: '', 
        mappedSections: [], 
      };
    }

    // --- Start of calculations moved inside useMemo ---
    const hasBusinessName = Boolean(company?.name);
    const hasWelcomeMessage = Boolean(company?.welcome_message);
    const hasGoodbyeMessage = Boolean(company?.goodbye_message);
    const hasMultipleStaff = (company?.users?.length || 0) > 1;
    const internalHasStaffWithRoles = !!company?.users?.some((member: ITeamMember) => Boolean(member.tags?.length)); // Always boolean
    const hasScheduledStaff = !!company?.users?.some((member: ITeamMember) => {
      if (member.status !== 'schedule' || !member.availability) return false;
      const availabilityDays = Object.values(member.availability) as IAvailabilityTime[];
      return availabilityDays.some(day => day.enabled);
    });
    const getTotalChars = () => {
      const cards = company?.cards || [];
      return cards.reduce((total: number, card: { title?: string; body?: string }) => {
        const titleLength = card.title?.length || 0;
        const bodyLength = card.body?.length || 0;
        return total + titleLength + bodyLength;
      }, 0);
    };
    const totalChars = getTotalChars();
    const internalHasValidBusinessInfo = totalChars >= BUSINESS_INFO_THRESHOLD; // Renamed internal calculation

    const sections = setupSections.map(section => ({
      ...section,
      todos: section.todos.map(todo => {
        const apiCompleted = isTaskCompleted(todo.id);
        const apiSkipped = isTaskSkipped(todo.id);
        let uiCompleted = false;
        switch (todo.id) {
          case 'business-name': uiCompleted = hasBusinessName; break;
          case 'welcome-message': uiCompleted = hasWelcomeMessage; break;
          case 'goodbye-message': uiCompleted = hasGoodbyeMessage; break;
          case 'business-info': uiCompleted = internalHasValidBusinessInfo; break;
          case 'staff-add': uiCompleted = hasMultipleStaff; break;
          case 'staff-roles': uiCompleted = internalHasStaffWithRoles; break;
          case 'staff-scheduling': uiCompleted = hasScheduledStaff; break;
          default: uiCompleted = false;
        }
        const isCompleted = uiCompleted || apiCompleted;
        return { 
          ...todo, 
          completed: isCompleted,
          skipped: apiSkipped 
        };
      })
    }));

    const totalTodos = sections.reduce((total, section) => total + section.todos.length, 0);
    const completedTodos = sections.reduce((total, section) => 
      total + section.todos.filter(todo => todo.completed).length, 0);
    const calculatedProgress = totalTodos > 0 ? Math.round((completedTodos / totalTodos) * 100) : 0;
    const { title: calculatedTitle, description: calculatedDescription } = getProgressContent(calculatedProgress);

    return {
      progress: calculatedProgress,
      title: calculatedTitle,
      description: calculatedDescription,
      mappedSections: sections,
    };
  }, [loadingOnboarding, company, isTaskCompleted, isTaskSkipped]); // Remove onboardingProgress as it's not actually needed

  // Render loading state
  if (loadingOnboarding) {
    return (
      <Paper shadow="xs" p="lg">
        <Stack>
          <Skeleton height={20} width="40%" mb="sm" />
          <Skeleton height={12} width="70%" mb="sm" />
          <Skeleton height={10} width="100%" mb="lg" />
          <Skeleton height={50} mb="md" />
          <Skeleton height={50} mb="md" />
          <Skeleton height={50} />
        </Stack>
      </Paper>
    );
    }

  // Render error state
  if (errorOnboarding) {
    return (
      <Paper shadow="xs" p="lg">
        <Stack>
          <Text size="sm" c="red">
            {errorOnboarding}
          </Text>
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper withBorder p="md" radius="md">
      <Stack>
        <Grid gutter="lg">
          <Grid.Col span={6}>
            <Stack gap={4}>
              <Text fw={600} size="xl">{calculatedTitle}</Text>
              <Text c="gray.6">{calculatedDescription}</Text>
            </Stack>
          </Grid.Col>
          <Grid.Col span={6}>
            <Stack justify="center" h="100%">
              <Flex align="center" gap="md">
                <Progress 
                  value={calculatedProgress} 
                  size="xl"
                  styles={{
                    root: {
                      backgroundColor: 'var(--mantine-color-gray-2)',
                      flex: 1
                    },
                    section: {
                      backgroundColor: calculatedProgress >= 80 ? 'var(--green-color)' : 'var(--yellow-color)'
                    }
                  }}
                />
                <Text fw={600} size="xl">{calculatedProgress}%</Text>
              </Flex>
            </Stack>
          </Grid.Col>
        </Grid>

        <Accordion 
          value={activeAccordionItem}
          onChange={(val: string | null) => setActiveAccordionItem(typeof val === 'string' ? val : null)}
          variant="contained"
          styles={{
            item: {
              backgroundColor: 'transparent',
              border: '1px solid var(--mantine-color-gray-3)',
              borderRadius: 'var(--mantine-radius-md)',
              marginTop: 'var(--mantine-spacing-md)',
              overflow: 'hidden'
            },
            control: {
              padding: 'var(--mantine-spacing-md)',
              '&:hover': {
                backgroundColor: 'transparent'
              }
            },
            content: {
              backgroundColor: 'var(--mantine-color-gray-0)',
              padding: 'var(--mantine-spacing-xl)',
              paddingTop: 'var(--mantine-spacing-lg)'
            },
            chevron: {
              color: 'var(--mantine-color-dark-3)',
              '&[data-rotate]': {
                transform: 'rotate(180deg)'
              }
            },
            label: {
              padding: 0
            }
          }}
        >
          {calculatedSections.map((section: { id: string; title: string; todos: TodoItem[] }) => (
            <Accordion.Item key={section.id} value={section.id}>
              <Accordion.Control>
                <Group gap="sm" wrap="nowrap">
                  <Text fw={600} size="lg">{section.title}</Text>
                  {section.todos.length > 0 && (
                    section.todos.every(todo => todo.completed) ? (
                      <Group gap={6} align="center">
                      <ThemeIcon 
                        color="transparent"
                        size={24} 
                        radius="xl"
                        styles={{
                          root: {
                            border: '2px solid var(--green-color)'
                          }
                        }}
                      >
                        <IconCheck style={{ color: 'var(--green-color)' }} size={14} />
                    </ThemeIcon>
                        {/* Add skipped counter if any skipped tasks */}
                        {section.todos.some(todo => todo.skipped) && (
                          <Badge
                            variant="light"
                            color="gray"
                            size="lg"
                            radius={999}
                            style={{
                              backgroundColor: '#f8f9fa',
                              border: '2px solid var(--mantine-color-gray-5)',
                              color: '#1a1a1a',
                              width: 'auto',
                              minWidth: 24,
                              padding: '0 6px',
                              fontWeight: 'var(--font-weight-normal)'
                            }}
                          >
                            {section.todos.filter(todo => todo.skipped).length} skipped
                          </Badge>
                        )}
                      </Group>
                    ) : (
                      <Group gap={6} align="center">
                        {/* Incomplete Counter */}
                      <Badge
                        variant="light"
                        color="yellow"
                        size="lg"
                        radius={999}
                        style={{
                          backgroundColor: 'var(--yellow-light-bg)',
                              border: '2px solid var(--yellow-border)',
                              color: '#1a1a1a',
                              width: 'auto',
                              minWidth: 24,
                              padding: '0 6px',
                              fontWeight: 'var(--font-weight-normal)'
                            }}
                        >
                          {section.todos.filter(todo => !todo.completed).length} remaining
                        </Badge>
                        
                        {/* Skipped Counter (when some incomplete) */}
                        {section.todos.some(todo => todo.skipped) && (
                          <Badge
                            variant="light"
                            color="gray"
                            size="lg"
                            radius={999}
                            style={{
                              backgroundColor: '#f8f9fa',
                              border: '2px solid var(--mantine-color-gray-5)',
                            color: '#1a1a1a',
                            minWidth: 24,
                                padding: '0 6px',
                                fontWeight: 'var(--font-weight-normal)'
                        }}
                      >
                            {section.todos.filter(todo => todo.skipped).length} skipped
                      </Badge>
                        )}
                      </Group>
                    )
                  )}
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap={0} mt={0}>
                  {section.todos.map((todo: TodoItem, index: number) => (
                    <Box 
                      key={todo.id} 
                      pos="relative" 
                      pb={index < section.todos.length - 1 ? "lg" : 0}
                    >
                      {index < section.todos.length - 1 && (
                        <Box
                          pos="absolute"
                          left={12}
                          top={24}
                          h="calc(100% - 24px)"
                          w={2}
                          bg="gray.3"
                        />
                      )}
                      <Group align="flex-start" gap="sm" wrap="nowrap">
                        <Box>
                          {todo.completed ? (
                            <ThemeIcon 
                              color="transparent"
                              size={24} 
                              radius="xl"
                              styles={{
                                root: {
                                  border: `2px solid ${todo.skipped ? 'var(--mantine-color-gray-5)' : 'var(--green-color)'}`
                                }
                              }}
                            >
                              {todo.skipped ? (
                                <Text size="xs" c="gray.6" fw={500}>S</Text>
                              ) : (
                              <IconCheck style={{ color: 'var(--green-color)' }} size={14} />
                              )}
                            </ThemeIcon>
                          ) : (
                            <ThemeIcon 
                              size={24} 
                              radius="xl"
                              styles={{
                                root: {
                                  border: '2px solid var(--yellow-border)',
                                  backgroundColor: 'var(--yellow-light-bg)'
                                }
                              }}
                            >
                              <IconExclamationMark style={{ color: 'var(--mantine-color-yellow-5)' }} size={14} />
                            </ThemeIcon>
                          )}
                        </Box>
                        <Box style={{ flex: 1 }}>
                          <Group justify="space-between" align="flex-start">
                          <Text fw={500} size="sm" mb={4}>{todo.title}</Text>
                            {todo.skipped && (
                              <Badge 
                                variant="outline" 
                                color="gray" 
                                size="sm"
                                style={{
                                  textTransform: 'none',
                                }}
                              >
                                Skipped
                              </Badge>
                            )}
                          </Group>
                          <Text size="sm" c="gray.6" style={{ lineHeight: 1.5 }}>
                            {todo.description}
                </Text>
                          {!todo.completed && getNavigationConfig(todo.id) && (
                            <Group gap="xs" mt="sm">
                            <Button
                              variant="subtle"
                              size="sm"
                              type="button"
                              leftSection={<IconArrowRight size={16} />}
                                onClick={() => {
                                  // Try to mark as completed via API first, then navigate
                                  handleNavigation(todo.id);
                                }}
                              styles={{
                                root: {
                                  color: 'var(--primary-color)',
                                  '&:hover': {
                                    backgroundColor: 'var(--primary-color-light)'
                                  }
                                }
                              }}
                            >
                              Complete this step
                            </Button>
                              
                              {/* Add a separator */}
                              {!todo.completed && (
                                <Box 
                                  h={20} 
                                  w={1} 
                                  bg="gray.3" 
                                  mx={8}
                                  style={{display: 'inline-block'}}
                                />
                              )}
                              
                              {/* Add Skip button next to Complete this step button */}
                              {!todo.skipped && (
                                <Button
                                  variant="subtle"
                                  color="gray"
                                  size="sm"
                                  type="button"
                                  onClick={() => handleSkipTask(todo.id)}
                                  style={{
                                    fontWeight: 'var(--font-weight-normal)'
                                  }}
                                >
                                  Skip
                                </Button>
                              )}
                              
                              {/* Add Unskip button for skipped items */}
                              {todo.skipped && (
                                <Button
                                  variant="subtle"
                                  color="gray"
                                  size="sm"
                                  type="button"
                                  onClick={() => handleMarkComplete(todo.id, false)}
                                  style={{
                                    fontWeight: 'var(--font-weight-normal)'
                                  }}
                                >
                                  Unskip
                                </Button>
                              )}
                            </Group>
                          )}
                          
                          {/* Show Unskip button for skipped items with no navigation config or with completed status */}
                          {todo.skipped && (!getNavigationConfig(todo.id) || todo.completed) && section.id !== 'share' && (
                            <Group gap="xs" mt="sm">
                              <Button
                                variant="subtle"
                                color="gray"
                                size="sm"
                                type="button"
                                onClick={() => handleMarkComplete(todo.id, false)}
                                style={{
                                  fontWeight: 'var(--font-weight-normal)'
                                }}
                              >
                                Unskip
                              </Button>
                            </Group>
                          )}
                          
                          {/* Add controls for Share section items */}
                          {section.id === 'share' && (
                            <Group gap="xs" mt="sm">
                              <Checkbox
                                checked={todo.completed && !todo.skipped}
                                onChange={() => {
                                  // If item is skipped, force to completed state
                                  if (todo.skipped) {
                                    handleMarkComplete(todo.id, true);
                                  } else {
                                    // Otherwise toggle normally
                                    handleMarkComplete(todo.id, !todo.completed);
                                  }
                                }}
                                style={{
                                  cursor: 'pointer',
                                  // Match checkbox styling to screenshot
                                  '&:checked': {
                                    backgroundColor: 'var(--green-color)',
                                    borderColor: 'var(--green-color)'
                                  }
                                }}
                              />
                              <Text 
                                size="sm" 
                                onClick={() => {
                                  // If item is skipped, force to completed state
                                  if (todo.skipped) {
                                    handleMarkComplete(todo.id, true);
                                  } else {
                                    // Otherwise toggle normally
                                    handleMarkComplete(todo.id, !todo.completed);
                                  }
                                }} 
                                style={{ cursor: 'pointer', paddingRight: 18 }}
                              >
                                Mark complete
                              </Text>
                              
                              {/* Add a separator */}
                              {!todo.completed && (
                                <Box 
                                  h={20} 
                                  w={1} 
                                  bg="gray.3" 
                                  mx={8}
                                  style={{display: 'inline-block'}}
                                />
                              )}
                              
                              {!todo.completed && !todo.skipped && (
                                <Button
                                  variant="subtle"
                                  color="gray"
                                  size="sm"
                                  type="button"
                                  onClick={() => handleSkipTask(todo.id)}
                                  style={{
                                    fontWeight: 'var(--font-weight-normal)'
                                  }}
                                >
                                  Skip
                                </Button>
                              )}
                              
                              {todo.skipped && (
                                <Button
                                  variant="subtle"
                                  color="gray"
                                  size="sm"
                                  type="button"
                                  onClick={() => handleMarkComplete(todo.id, false)}
                                  style={{
                                    fontWeight: 'var(--font-weight-normal)'
                                  }}
                                >
                                  Unskip
                                </Button>
                              )}
                            </Group>
                          )}
                        </Box>
                      </Group>
                    </Box>
                  ))}
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          ))}
        </Accordion>
      </Stack>
    </Paper>
  );
} 